import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalIcon, X } from "lucide-react";

/**
 * DatePicker
 * Popover-based date picker using shadcn Calendar.
 *
 * Design notes on the corner-flicker fix (2026-07-09):
 *   The trigger is a plain <button> — NOT the shadcn <Button> component.
 *   `<Button variant="outline">` ships `hover:bg-accent hover:text-accent-foreground`
 *   which combined with our inline `hover:bg-white` created two competing
 *   hover states on the same element. Combined with `shadow-sm` (which
 *   inflates the paint region by a few pixels), moving the mouse near the
 *   button edge could enter/exit the hover region rapidly → flicker.
 *   Using a raw <button> with a single, quiet hover state kills it.
 *
 *   Also: the clear-X is a SIBLING of the trigger (not nested inside it).
 *   Nested interactive elements caused overlapping hover regions in the
 *   upper-left corner of the icon.
 *
 * Props:
 *   value       — "YYYY-MM-DD" string or ""
 *   onChange    — (isoStr: string) => void   ("" when cleared)
 *   placeholder — e.g. "Pick date"
 *   testId      — data-testid for the trigger
 *   className   — passed to the trigger button
 */
export default function DatePicker({ value, onChange, placeholder = "Pick date", testId, className = "" }) {
  const [open, setOpen] = useState(false);
  const parsed = value ? new Date(value + "T00:00:00") : undefined;

  const fmt = (d) => {
    if (!d) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const displayLabel = parsed
    ? parsed.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
    : placeholder;

  return (
    <div className={`relative inline-flex items-center gap-1 w-full ${className}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            className={
              // Plain button — single explicit hover state, no shadow. Matches
              // the `.glass-input` look but avoids the shadcn `outline`
              // variant's `hover:bg-accent` cascade that caused corner
              // flicker.
              "flex-1 min-w-0 h-9 px-3 rounded-[0.6rem] " +
              "bg-white border border-slate-200 " +
              "text-sm font-normal text-left " +
              "flex items-center justify-between gap-1.5 " +
              "focus:outline-none focus:ring-2 focus:ring-slate-300 " +
              "hover:border-slate-300 " +
              (parsed ? "text-slate-900 " : "text-slate-500 ")
            }
          >
            <span className="flex items-center gap-1.5 truncate">
              <CalIcon className="w-3.5 h-3.5 text-slate-500 shrink-0" />
              <span className="truncate">{displayLabel}</span>
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={parsed}
            onSelect={(d) => {
              onChange(fmt(d));
              setOpen(false);
            }}
            defaultMonth={parsed || new Date()}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {parsed && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(""); }}
          className="p-1 rounded shrink-0 text-slate-500 hover:text-slate-800 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
          aria-label="Clear date"
          data-testid={testId ? `${testId}-clear` : undefined}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
